import {
  DataGridPremium,
  type GridColDef,
  type GridInitialState,
  type GridRenderCellParams,
  // GridFooterContainer,
} from '@mui/x-data-grid-premium';
import { useAppSelector } from 'hooks/redux';
import { DateTime } from 'luxon';
import React from 'react';

import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import { TypographyCell } from 'components/DataGridCells//TypographyCell';
import { QAmountFieldCell } from 'components/DataGridCells/QAmountFieldCell';
import useStyles from 'containers/Settings/ChildPages/RulesSimplifi/styles';

import { Typography } from '@mui/material';
import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import type { TransactionRuleType } from 'app-components/flux/transaction-rules/transactionRulesTypes';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

const sx = {
  '& .MuiDataGrid-row': {
    borderRadius: '8px',
  },
  '& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-hovered': {
    backgroundColor: 'transparent',
    cursor: 'default',
  },
  '& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-main': {
    marginTop: 0, // prevent space for header
  },
  '& .MuiDataGrid-columnHeaders': {
    display: 'none',
  },
};

const PreviewFooter = () => {
  const { classes } = useStyles();

  return (
    <Box className={classes.previewFooter}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant='titleRegular'>End of sample matches</Typography>
        <Typography variant='bodyRegular'>There may be more transactions that your rule can apply to</Typography>
      </Box>
    </Box>
  );
};

const initialState: GridInitialState = {
  sorting: {
    sortModel: [{ field: 'date', sort: 'desc' }],
  },
};

const RulePreviewTxnList = ({
  txns,
  txnRule,
  sinceDate,
}: {
  txns: TransactionProps[];
  txnRule: TransactionRuleType;
  sinceDate?: string;
}) => {
  const { classes } = useStyles();

  const applyToolTipText =
    txnRule.payeeSource?.payeeType === 'STATEMENT' ? 'Original statement name:' : 'Quicken name:';
  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const categoriesById = useAppSelector(categoriesSelectors.getCategoriesById);

  const renderZeroState = (sinceDate?: string) => {
    const sinceText = sinceDate ? `No sample matches to show since ${sinceDate}` : `No sample matches to show`;

    return (
      <Box className={classes.zeroStateView}>
        <Typography variant='titleRegular'>{sinceText}</Typography>
        <Typography variant='bodyRegular'>There may be earlier transactions that your rule can apply to</Typography>
      </Box>
    );
  };

  const columns: GridColDef[] = React.useMemo(() => {
    return [
      {
        field: 'account',
        headerName: 'account',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <TypographyCell params={params}>{accountsById?.get(params.value || '')?.name}</TypographyCell>
        ),
      },
      {
        field: 'date',
        type: 'date',
        headerName: 'date',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <TypographyCell params={params}>{DateTime.fromJSDate(params.value).toFormat('LLL dd, yyyy')}</TypographyCell>
        ),
      },
      {
        field: 'payee',
        headerName: 'payee',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <Box className={classes.payeeName}>
            <Tooltip
              arrow
              placement='right'
              title={`${applyToolTipText} ${params.row.tooltipName}`}
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 420,
                    padding: 2,
                  },
                },
              }}
            >
              <InfoIcon className={classes.infoIcon16} />
            </Tooltip>
            <TypographyCell params={params} />
          </Box>
        ),
      },
      {
        field: 'category',
        headerName: 'Category',
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <TypographyCell params={params}>{params.formattedValue.split(':').pop()}</TypographyCell>
        ),
      },
      {
        field: 'amount',
        headerName: 'Amount',
        flex: 1,
        align: 'right',
        renderCell: (params: GridRenderCellParams) => (
          <QAmountFieldCell params={params} showColor='positive' expectedSign='negative' />
        ),
      },
    ];
  }, [accountsById, applyToolTipText, classes.infoIcon16, classes.payeeName]);

  const rows = React.useMemo(() => {
    type TransactionRow = {
      id?: string;
      date: Date;
      account?: string;
      payee: string;
      tooltipName: string;
      category: string;
      amount: number;
    };

    return txns.reduce<TransactionRow[]>((acc, item) => {
      acc.push({
        id: item.id || undefined,
        account: item.accountId ?? '',
        date: DateTime.fromISO(item.postedOn ?? '').toJSDate(),
        payee: item.payee ?? 'N/A',
        tooltipName:
          (txnRule.payeeSource?.payeeType === 'STATEMENT' ? item.cpData?.payee : item.cpData?.inferredPayee) || '',
        category: chartOfAccountsUtils.getCoaString(item.coa, categoriesById, accountsById, false),
        amount: item.amount ?? 0,
      });

      return acc;
    }, []);
  }, [accountsById, categoriesById, txnRule, txns]);

  return (
    <>
      {rows && rows.length ? (
        <div className={classes.gridContainer}>
          <DataGridPremium
            hideFooter
            columns={columns}
            rows={rows}
            columnHeaderHeight={0}
            sx={sx}
            initialState={initialState}
            disableRowSelectionOnClick
            rowSelection={false}
          />
          <PreviewFooter></PreviewFooter>
        </div>
      ) : (
        <>{renderZeroState(sinceDate)}</>
      )}
    </>
  );
};

export default RulePreviewTxnList;
